<script setup>
import { onMounted, computed, ref } from 'vue';
import { useProductStore } from '../Product/Composables/Store';
import { productVariant, getSecondaryProducts } from './Composables/ProductCard.js';
import ReviewStars from './ReviewStars.vue';
import { formatID } from '@utils/format';
import ProductCardSwatches from './ProductCardSwatches.vue';
import Price from '../Global/Price.vue';
import Eyebrow from '../Global/Eyebrow.vue';
import ProductModal from '../Product/ProductModal.vue';
import Icon from './Icon.vue';
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  terms: Object,
  selectionListener: Boolean,
  ready: {
    type: Boolean,
    default: false,
  },
});

const modalItem = ref(false);
const store = useProductStore();
const productId = formatID(props?.item?.id) || '';
let variant = window.location.pathname.includes('/collections') ? props?.item?.featuredSku?.reference : props?.item?.selectedVariant;
const variantId = variant?.id ? formatID(variant?.id) : productId;
store.mountPromoListener();

const quickViewQualifier = computed(() => {
  const isBundle = props.item.tags.some((tag) => tag == 'Product Bundle');
  return !isBundle;
});

const isSingleProd = computed(() => props.item.options.find((option) => option.values.includes('Default Title')));

const quickViewText = computed(() => {
  let text = isSingleProd.value ? 'Add' : 'View';
  return `Quick ${text}`;
});

const showReviews = computed(() => !props.item.tags.includes('Hide Zero Reviews'));
const reviewsProduct = computed(() => {
  let tag = props.item.tags.filter((tag) => tag.includes('Review ProductID'));
  return tag.length > 0 ? tag[0].split(': ')[1] : props.item.id.split('/Product/')[1];
});

const findHandleBySelection = (selection) => {
  // Check if the selection exists in the primary swatches
  if (swatches?.value?.includes(selection)) {
    return props.item.handle;
  }

  for (const item of props.item.secondaryItems) {
    if (item.swatches?.values?.includes(selection)) {
      return item.handle; // Return the handle of the matching secondary item
    }
  }
};

const updateCard = async (selection) => {
  const options = props.item.selectedOptions || [];

  if (options.length <= 0) return;

  options.filter((option) => option.name === selection.name).map((option) => (option.value = selection.value));

  const handle = findHandleBySelection(selection.value);

  const variant = await productVariant.fromStorefront(handle, options);
  props.item.selectedVariant = variant;
};

const url = computed(() => {
  const handle = props.item.handle.split('?')[0].trim();
  const base = `/products/${handle}`;
  if (!props?.item?.selectedVariant) return base;
  const item = props.item;
  const tags = item.tags ?? [];

  const isBundle = tags.includes('Product Bundle') ?? false;
  const isSteppedBundle = isBundle && !tags.includes('Auto-Bundle');
  const selectedVariant = item.selectedVariant.id;
  const parsedId = selectedVariant.split('ProductVariant/')[1].trim();
  const name = isSteppedBundle ? `component0` : `variant`;
  const params = `?${name}=${parsedId}`;

  return `${base}${params}`;
});

const hoverImage = computed(() => {
  const variant = props?.item?.selectedVariant ?? false;

  if (!variant) return false;
  const image = variant?.hoverImage?.reference?.image ?? false;
  return image;
});

const swatches = computed(() => {
  const values = props.item?.swatches ?? [];
  return Array.from(new Set(values));
});

const shortDescription = computed(() => {
  const customDescription = props?.item?.customDesc ?? '';
  if (customDescription !== '') {
    return customDescription;
  } else {
    return props.item?.shortDescription?.value ?? false;
  }
});

const displayShortDescription = computed(() => {
  const tags = props?.item?.tags ?? [];
  const doesNotContainTag = !tags.includes('Hide PC Includes');
  return doesNotContainTag && shortDescription.value;
});

const hiddenOptions = () => {
  const tags = props?.item?.tags ?? [];
  return tags.filter((tag) => tag.includes('Hide Option Value:')).map((tag) => tag.split(':')[1].trim());
};

const image = (context) => props.item.selectedVariant.image[context];

const title = () => (props.item.customTitle !== '' ? props.item.customTitle : props.item.title);

const badgeText = () => (props.item.badgeText && props.item.badgeText !== 'false' && props.item.badgeText !== '' ? props.item.badgeText : false);
const badgeImage = () => (props.item.badgeAsset && props.item.badgeAsset !== 'false' && props.item.badgeAsset !== '' ? props.item.badgeAsset : false);

const toggleModal = (product) => {
  if (modalItem.value && modalItem.value.edit) {
    modalItem.value = false;
  }

  if (product) {
    modalItem.value = product;
    modalItem.value.edit = !modalItem.value.edit;
  }
};

const handleQuickAction = () => {
  const isSingleProduct = isSingleProd.value;
  const item = JSON.parse(JSON.stringify(props.item));
  return isSingleProduct ? quickAdd(item) : toggleModal(item);
};

const createEvent = (items) =>
  new CustomEvent('theme:add-to-cart', {
    detail: items,
  });

const quickAdd = (item) => {
  const promo = window.Brooklinen?.promo?.discounts ?? {};
  const categoryId = promo?.category?.promoName ?? '';
  let variant = window.location.pathname.includes('/collections') ? item.variants[0] : item.selectedVariant;
  const id = variant.id.split('ProductVariant/')[1].toString().trim();
  const comparePrice = variant?.compareAtPrice?.amount;
  const price = variant.price.amount;
  const tags = item.tags;
  const originalPrice = comparePrice > 1 ? comparePrice : price;
  const tprReduction = comparePrice > 1;

  const isCategory = () => tags.some((tag) => tag.includes('Category Promo:'));
  const isLastCall = () => tags.includes('Last Call Assortment');

  // Temporary Price Reductions are variants that get a reduction but are not part of any active sale ex: category sale
  let discount, categoryDiscount, tprDiscount;
  if (tprReduction && !isCategory() && !isLastCall()) {
    tprDiscount = 100 - (price / comparePrice) * 100;
  }

  const categoryPromoName = isCategory() ? categoryId : '';
  if (isCategory()) {
    categoryDiscount = tags
      .find((tag) => tag.includes('Category Promo:'))
      ?.split(':')[1]
      .trim();
  }
  if (isLastCall()) {
    discount = tags
      .find((tag) => tag.includes('Discount Amount:'))
      ?.split(':')[1]
      .trim();
  }

  let payload = {
    id: parseInt(id),
    quantity: 1,
    properties: {
      _msrp: originalPrice,
      _category_id: categoryPromoName,
      _category_discount: categoryDiscount,
      _isGiftCard: false,
      Ships: variant?.shipsBy?.value,
      _lc_discount: discount,
      _tpr_discount: tprDiscount,
    },
  };
  const event = createEvent(payload);
  document.dispatchEvent(event);
};

const setTermSetting = () => {
  if (!store?.$state.terms) store.$state.terms = props.terms;

  if (!store?.$state.settings) {
    store.$state.settings = {
      max_inventory: 10,
    };
  }
};

onMounted(async () => {
  let variant = null;
  let options = null;

  let { featuredSku, handle } = props.item;

  // Fetch secondary items related to the product
  const secondaryItems = await getSecondaryProducts(handle);

  // Attempt to find options for the primary product
  options = productVariant.fromSku(handle, featuredSku);

  // if the featured sku was not found in the primary product
  if (!options) {
    for (const item of secondaryItems) {
      const secondaryOptions = productVariant.fromSku(item.handle, featuredSku);

      if (secondaryOptions) {
        options = secondaryOptions;
        variant = await productVariant.fromStorefront(item.handle, secondaryOptions);
        break; // Exit the loop if a valid variant is found
      }
    }
  } else {
    // If the featured sku is found within the primary product, fetch the variant normally
    variant = await productVariant.fromStorefront(handle, options);
  }

  // If no options or variants are found, default to first one
  if (!variant && !options) {
    variant = props.item.variants[0];
    options = variant.selectedOptions;
    featuredSku = variant.sku;
  }

  props.item.selectedOptions = options;
  props.item.selectedVariant = variant;
  props.item.featuredSku = featuredSku;
  props.item.secondaryItems = secondaryItems;

  setTermSetting();
});
</script>

<template>
  <div class="ProductCard" data-color="night-sky-dark" data-ht-component="product-card" :data-product-id="productId" :data-variant-id="variantId">
    <div class="relative" :class="quickViewQualifier ? 'ProductCard__MediaQV' : ''">
      <a :href="url">
        <span class="hidden">Product Card Media</span>
        <div :class="[quickViewQualifier ? 'ProductCard__Media--no-margin' : '', 'ProductCard__Media']">
          <template v-if="item.selectedVariant">
            <div class="ProductCard__Badges" v-if="badgeText() || badgeImage()">
              <div class="ProductCard__TextBadgeWrapper" v-if="badgeText()">
                <span data-ht-element="text-badge" class="ProductCard__TextBadge t5" v-html="badgeText()"></span>
              </div>
              <div class="ProductCard__ImgBadgeWrapper" v-if="badgeImage()">
                <img data-ht-element="image-badge" class="ProductCard__ImgBadge" :src="badgeImage()" :alt="'Award'" />
              </div>
            </div>
            <img :src="image('url')" :alt="image('altText') || 'Product Image'" class="ProductCard__ProductImg" />
            <img loading="lazy" v-if="hoverImage" :src="hoverImage.url" :alt="'Product Image'" class="ProductCard__ProductImg ProductCard__ProductImg--Hover js-variant" />
          </template>
          <span class="placeholder" v-else></span>
        </div>
      </a>
      <template v-if="quickViewQualifier">
        <button :data-ht-element="isSingleProd ? 'quick-add-button' : 'quick-view-button'" data-color="night-sky-dark" role="button" class="ProductCard__QuickView t5" @click="handleQuickAction">
          <Icon icon="qv-plus" />
          <span class="ProductCard__QuickView-Text">
            {{ quickViewText }}
          </span>
        </button>

        <Teleport to="#quickViewModal" v-if="modalItem.edit">
          <ProductModal @toggle-modal="toggleModal" :product="modalItem" :location="'quick-view'" :reviewsProduct="reviewsProduct" />
        </Teleport>
      </template>
    </div>
    <a :href="url">
      <Eyebrow :item="item" :ready="ready" :location="'product-card'" />
      <p class="t2" v-html="title()" data-ht-element="product-title"></p>
      <Price :item="item" :location="'product-card'" :ready="ready" />
      <div v-if="displayShortDescription" class="p4" v-html="shortDescription"></div>
      <ReviewStars v-if="showReviews" :product="reviewsProduct" :location="'product-card'" />
    </a>
    <ProductCardSwatches v-if="item.selectedOptions" @updateCard="updateCard" :swatches="swatches" :secondaryItems="item.secondaryItems" :all-options="item.options" :selected-options="item.selectedOptions" :title="item.title" :selection-listener="selectionListener" :hidden-options="hiddenOptions()" :url="url" />
  </div>
</template>
